import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';

import { AuthError, isUnauthorized } from '@/lib/api-service';
import { RedirectToLoginError } from '@/lib/auth';
import { getCartService } from '@/services/cart.service';
import { getTrendingKeywords } from '@/services/trendingKeywords.service';
import { fetchDataFromCookies } from '@/services/util/cookies.util';
import { Brand, SearchContextInterface, TopKeywords } from '@/types';
import { SuggestionCacheEntry } from '@/types';
import { CartResponse, ProductCounts } from '@/types/cart.types';
import { RecommendationsResponse } from '@/types/recommendation.types';
import {
  extractCartQuantities,
  getCartItemCount,
  getCookieOrSessionId,
} from '@/utils/cart.util';
import { isReactNativeWebView } from '@/utils/common';
import { hasRNWebViewMessaging } from '@/utils/platform.util';

const SearchContextDefaultValue: SearchContextInterface = {
  cartData: null,
  setCartData: () => null,
  productCounts: {},
  setProductCounts: () => null,
  topPicksRecommendations: null,
  topBrands: [],
  topKeywords: [],
  isBrandApiLoading: false,
  isLoading: false,
  suggestionCacheValue: null,
  setSuggestionCache: () => null,
};

const SearchContext = createContext<SearchContextInterface>(
  SearchContextDefaultValue
);

interface SearchContextProviderInterface {
  children: React.ReactNode;
}

const SearchContextProvider: React.FC<SearchContextProviderInterface> = ({
  children,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isBrandApiLoading, setIsBrandApiLoading] = useState<boolean>(true);
  const [cartData, setCartData] = useState<CartResponse | null>(null);
  const [productCounts, setProductCounts] = useState<ProductCounts>({});

  const [topPicksRecommendations] = useState<RecommendationsResponse | null>(
    null
  );
  const [topBrands, setTopBrands] = useState<Brand[]>([]);
  const [topKeywords, setTopKeywords] = useState<TopKeywords[]>([]);
  const [suggestionCacheValue, setSuggestionCacheValue] = useState<{
    query: string;
    data: SuggestionCacheEntry;
  } | null>(null);
  const [isLoading] = useState<boolean>(false);
  const { accessToken } = fetchDataFromCookies();
  const isAuth = searchParams.get('isAuth') === 'true';

  const count = useMemo(() => getCartItemCount(cartData), [cartData]);

  const setSuggestionCache = useCallback(
    (query: string, data: Partial<SuggestionCacheEntry>) => {
      setSuggestionCacheValue((prev) => {
        const normalizedQuery = query.trim().toLocaleLowerCase();
        const isSameQuery = prev?.query === normalizedQuery;
        const existingData = isSameQuery
          ? prev.data
          : {
              suggestion: [],
              productSuggestion: [],
              productSuggestionCount: 0,
              hasMore: false,
            };

        return {
          query: normalizedQuery,
          data: {
            ...existingData,
            ...data,
          },
        };
      });
    },
    []
  );

  useEffect(() => {
    const handlePageShow = async (event: PageTransitionEvent) => {
      if (event.persisted) {
        try {
          const storeId = getCookieOrSessionId();
          const response = await getCartService(
            storeId,
            !isReactNativeWebView()
          );
          if (response.data) {
            setCartData(response.data);
          }
        } catch (error) {
          console.error('Failed to refresh cart:', error);
        }
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  useEffect(() => {
    const fetchTrendingKeywords = async () => {
      const data = await getTrendingKeywords();
      setTopBrands(data?.topBrands);
      setTopKeywords(data?.topKeywords);
      setIsBrandApiLoading(false);
    };

    fetchTrendingKeywords();
  }, []);

  const lastNotifiedCountRef = useRef<number | null>(null);

  // Notify React Native — only when the count actually changes, not on every
  // cart object identity change. Keeps the JS↔native bridge quiet.
  useEffect(() => {
    if (!hasRNWebViewMessaging()) return;
    if (lastNotifiedCountRef.current === count) return;
    lastNotifiedCountRef.current = count;
    const rnWindow = (
      window as Window & {
        ReactNativeWebView?: { postMessage: (msg: string) => void };
      }
    ).ReactNativeWebView;
    rnWindow?.postMessage(
      JSON.stringify({ type: 'CART_COUNT_UPDATED', data: { value: count } })
    );
  }, [count]);

  useEffect(() => {
    if (cartData) {
      setProductCounts(extractCartQuantities(cartData));
    }
  }, [cartData]);

  useEffect(() => {
    if (!router.isReady) return;
    if (cartData) return;

    const fetchCart = async () => {
      try {
        if (!isReactNativeWebView()) {
          if (await isUnauthorized()) {
            return;
          }
        }
        if (!isAuth && (await isUnauthorized())) {
          return;
        }
        const storeId = getCookieOrSessionId();
        const response = await getCartService(storeId, !isReactNativeWebView());

        if (response.data) {
          setCartData(response.data);
        }
      } catch (error) {
        if (
          error instanceof AuthError ||
          error instanceof RedirectToLoginError
        ) {
          return;
        }
        console.error('Error fetching cart:', error);
      }
    };

    fetchCart();
  }, [router.isReady, accessToken, cartData]);

  const values: SearchContextInterface = useMemo(
    () => ({
      cartData,
      setCartData,
      productCounts,
      setProductCounts,
      topPicksRecommendations,
      topBrands,
      topKeywords,
      isBrandApiLoading,
      isLoading,
      suggestionCacheValue,
      setSuggestionCache,
    }),
    [
      cartData,
      productCounts,
      topPicksRecommendations,
      topBrands,
      topKeywords,
      isBrandApiLoading,
      isLoading,
      suggestionCacheValue,
      setSuggestionCache,
    ]
  );

  return (
    <SearchContext.Provider value={values}>{children}</SearchContext.Provider>
  );
};

export { SearchContext, SearchContextProvider };
